import { useState, useEffect } from "react"
import { useDebounce } from "../../../hooks/useDebounce"
import MTable from "../../../components/mtable"
import Title from "../../../components/Title"
import InitialsAvatar from "../../../components/InitialsAvatar"

const REASON_LABELS = {
    WrongPassword: 'Wrong password',
    UserNotFound: 'No account',
    AccountNotVerified: 'Not verified',
}

const REASON_FILTERS = [
    { label: 'All', value: undefined },
    ...Object.entries(REASON_LABELS).map(([value, label]) => ({ label, value })),
]

const USER_TYPE_LABELS = {
    Admin: 'Admin',
    CompanyManager: 'Manager',
    Participant: 'Participant',
}

function reasonChipClass(reason) {
    if (reason === 'WrongPassword') return 'primary-light-chip'
    if (reason === 'AccountNotVerified') return 'primary-dark-chip'
    return 'neutral-chip'
}

// Local time is right here: unlike the stats, nothing is grouped by date
const formatDateTime = (value) => new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

const MAPPER = { columns: [
    {
        label: 'User', width: '2fr',
        render: (item) => (
            <div className="match-participant">
                <InitialsAvatar firstName={item.user?.firstName} lastName={item.user?.lastName} />
                <div style={{ minWidth: 0 }}>
                    <p className="card-label medium ellipsis">
                        {item.user ? `${item.user.firstName ?? ''} ${item.user.lastName ?? ''}`.trim() || '—' : 'Unknown user'}
                    </p>
                    <p className="card-label-s neutral ellipsis">{item.email}</p>
                </div>
            </div>
        )
    },
    {
        label: 'Account type', width: '1fr', align: 'center',
        value: (item) => item.user ? USER_TYPE_LABELS[item.user.userType] ?? item.user.userType : '—'
    },
    {
        label: 'When', width: '1fr', align: 'center',
        value: (item) => formatDateTime(item.createdAt)
    },
    {
        label: 'Reason', width: '1fr', align: 'center',
        render: (item) => (
            <div className="flex justify-center">
                <span className={`chip transparent ${reasonChipClass(item.reason)}`}>{REASON_LABELS[item.reason] ?? item.reason}</span>
            </div>
        )
    },
]}

export default function FailedLogins() {
    const [search, setSearch] = useState('')
    const [reasonFilter, setReasonFilter] = useState(undefined)
    const [params, setParams] = useState({})
    const debouncedSearch = useDebounce(search, 250)

    useEffect(() => {
        setParams({
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(reasonFilter ? { reason: reasonFilter } : {})
        })
    }, [debouncedSearch, reasonFilter])

    return (
        <div className="page-xl-centered standard-padding showing">
            <div className="v-spacer-m">
                <Title>Failed Logins</Title>
                <p className="neutral card-label">Failed login attempts from the last 30 days, newest first.</p>
            </div>

            <div className="card input-wrapper v-spacer-s">
                <span className="material-symbols-outlined icon input-icon">search</span>
                <input
                    placeholder="Search by name or email…"
                    value={search}
                    className="w-100"
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="card flex gap-xs wrap v-spacer-s">
                {REASON_FILTERS.map(f => (
                    <button
                        key={f.label}
                        className={`chip ${reasonFilter === f.value ? 'solid' : 'outline'}`}
                        onClick={() => setReasonFilter(f.value)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <MTable
                path="auth/failed-logins"
                queryParams={params}
                mapper={MAPPER}
                itemsPerPage={20}
                emptyState={<div className="dashed-card standard-padding">
                    <p className="card-title">No failed logins</p>
                    <p className="neutral card-label">Nothing matches in the last 30 days.</p>
                </div>}
            />
        </div>
    )
}